import { useBreakpointValue } from '@genial/utils';
import React from 'react';

import { InputMoneyProps } from '../../../../../../../components/InputMoney';
import { AssetSelectedTypeMap } from '../../../../../../../store/product/types';
import { useFixedIncomeAddedProductItem } from '../../../../../hooks';
import {
  AddedProductItemList,
  AddedProductItemRouteParamTypeProps,
  AssetWithErrorResponse,
} from '../../../../../utils/types';
import { FixedIncomeAssetController } from '../../../../DynamicAsset/hooks/products/FixedIncome';
import { AddedProductItemContainer } from '../Container';

import { AddedProductItemFixedIncomeDesktop } from './desktop';
import { AddedProductItemFixedIncomeMobile } from './mobile';
import { AddedProductItemFixedIncomeSkeleton } from './skeleton';
import { AddedProductItemFixedIncomeTablet } from './tablet';

type FixedIncomeDataProps = AssetSelectedTypeMap['fixedIncome'] & AssetWithErrorResponse;
export interface AddedProductItemFixedIncomeProps {
  data: FixedIncomeDataProps;
  type: AddedProductItemRouteParamTypeProps;
  isInvalid?: boolean;
  orderIsRecused?: boolean;
}

export interface AddedProductResponsiveItemFixedIncomeProps {
  data: FixedIncomeDataProps;
  inputMoneyDataProps: InputMoneyProps;
  messageAdjustUnitPriceIsVisible: boolean;
  inputIsFocused: boolean;
  onRemoveProduct: () => void;
  minApplication: number;
  ableToChangeFees: FixedIncomeAssetController['ableToChangeFees'];
  selectedFee: FixedIncomeAssetController['selectedFee'];
  selectedFeeId: FixedIncomeAssetController['selectedFeeId'];
  setSelectedFeeId: FixedIncomeAssetController['setSelectedFeeId'];
  estimatedIncomeCalculated: number;
  estimatedNetValueCalculated: number;
}

export const AddedProductItemFixedIncome: React.FC<AddedProductItemFixedIncomeProps> = ({
  data,
  type,
  isInvalid,
  orderIsRecused,
}) => {
  const {
    inputMoneyDataProps,
    inputIsFocused,
    messageAdjustUnitPriceIsVisible,
    isLoadingAction,
    onRemoveProduct,
    ableToChangeFees,
    selectedFee,
    selectedFeeId,
    setSelectedFeeId,
    minApplication,
    estimatedIncomeCalculated,
    estimatedNetValueCalculated,
  } = useFixedIncomeAddedProductItem({ data, type });

  const lateralBarBackgroundColor = isInvalid ? 'feedback.attention.lightest' : 'product.rf';

  const productDisplayNormalized =
    data.productDisplay && data.issuerName
      ? `${data.productDisplay} ${data.issuerName}`
      : data.productDisplayNormalized;

  const params = {
    lateralBarBackgroundColor,
    data: { ...data, productDisplayNormalized },
    inputMoneyDataProps,
    inputIsFocused,
    messageAdjustUnitPriceIsVisible,
    isLoadingAction,
    onRemoveProduct,
    ableToChangeFees,
    selectedFee,
    selectedFeeId,
    setSelectedFeeId,
    minApplication,
    estimatedIncomeCalculated,
    estimatedNetValueCalculated,
  };

  const layout = useBreakpointValue({
    base: <AddedProductItemFixedIncomeMobile {...params} />,
    sm: <AddedProductItemFixedIncomeTablet {...params} />,
    lg: <AddedProductItemFixedIncomeDesktop {...params} />,
  });

  if (isLoadingAction) {
    return <AddedProductItemFixedIncomeSkeleton />;
  }

  return (
    <AddedProductItemContainer
      data={data as AddedProductItemList}
      isInvalid={!!isInvalid}
      key={data.productId}
      orderIsRecused={!!orderIsRecused}
      lateralBarBackgroundColor={lateralBarBackgroundColor}>
      {layout}
    </AddedProductItemContainer>
  );
};
